import {
  ChangeEventHandler,
  FormEventHandler,
  forwardRef,
  RefForwardingComponent,
  useState,
} from 'react'
import styled from 'styled-components'
import {
  GqlNewsItemCommentListDocument,
  useCurrentUserQuery,
  useGqlCreateNewsCommentMutation,
} from 'generated/graphql'
import Avatar from '@app.components/Avatar/Avatar'
import Button from '@app.components/Button'
import Textarea from '@app.components/inputs/Textarea'
import useChannelError from '@app.feature/channel/hook/useChannelError'
import useIsMobile from '@app.modules/hooks/useIsMobile'
import { convertStringToContent } from '@app.modules/util'
import LoginAlert from 'components/LoginAlert'
import createNewsChildCommentUpdater from '../../gql/updater/createNewsChildCommentUpdater'
import createNewsCommentUpdater from '../../gql/updater/createNewsCommentUpdater'

// todo currentUser 기반으로 프로필 사진 표시
type TProps = {
  newsItemId: string
  parentId?: string
  viewerCanReply: boolean
  className?: string
  expendComment?: boolean
  onSuccess?: () => void
}

const NewsItemCommentForm: RefForwardingComponent<
  HTMLTextAreaElement,
  TProps
> = (
  {
    newsItemId,
    parentId,
    viewerCanReply,
    className,
    expendComment = false,
    onSuccess = () => {},
  },
  ref,
) => {
  const isMobile = useIsMobile()

  const { data } = useCurrentUserQuery()
  const [loginModalOpened, setLoginModalOpened] = useState(false)
  const onError = useChannelError()

  const [input, setInput] = useState('')
  const [error, setError] = useState('')

  const createNewsCommentMutation = useGqlCreateNewsCommentMutation({
    onCompleted: () => {
      onSuccess()
    },
    onError,
  })
  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    const content = input.trim()
    if (content === '') return
    const [createNewsComment] = createNewsCommentMutation

    if (parentId) {
      const variables = {
        input: {
          content,
          newsItemId,
          parentId,
        },
      }
      createNewsComment({
        variables,
        update: createNewsChildCommentUpdater(newsItemId, parentId),
      })
    }
    // 댓글
    else {
      const variables = {
        input: {
          content: input,
          newsItemId,
        },
      }
      createNewsComment({
        variables,
        refetchQueries: [
          {
            query: GqlNewsItemCommentListDocument,
            variables: {
              newsItemId,
              commentFirst: isMobile || expendComment ? 10 : 5,
              commentAfter: null,
            },
          },
        ],
        update: createNewsCommentUpdater(newsItemId),
      })
    }
    setInput('')
  }

  const handleChangeInput: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    const value = e.target.value
    if (value.length > 1000)
      setError(`최대 1000자까지 입력할 수 있습니다.(현재 ${value.length}자)`)
    else setError('')
    setInput(value)
  }

  const avatarType = data?.currentUser?.channel ? 'channel' : 'user'
  const avatarSrc =
    avatarType === 'user'
      ? data?.currentUser?.avatarUrl
      : data?.currentUser?.channel?.owner?.logoImage?.url

  const handleClick = () => {
    if (onError()) return
    if (!viewerCanReply) {
      setLoginModalOpened(true)
    }
  }
  return (
    <>
      <StyledWrapper className={className} onClick={handleClick}>
        <form onSubmit={handleSubmit} data-error={!!error}>
          <Avatar size={40} type={avatarType} src={avatarSrc} />
          <div className="input-wrapper">
            <div className="height">{convertStringToContent(input)}</div>
            <Textarea
              placeholder={
                viewerCanReply
                  ? '댓글을 입력하세요.'
                  : '로그인 후 댓글을 작성할 수 있습니다.'
              }
              autoComplete="off"
              spellCheck={false}
              className="comment-input"
              name={'input'}
              value={input}
              error={error}
              onChange={handleChangeInput}
              disabled={!viewerCanReply}
              ref={ref}
            />
          </div>
          <div className="btn-wrapper">
            <Button
              className="btn"
              type="submit"
              disabled={input.trim() === '' || !viewerCanReply || !!error}
            >
              등록
            </Button>
          </div>
        </form>
      </StyledWrapper>
      <LoginAlert
        DialogProps={{
          open: loginModalOpened,
        }}
        onRequestClose={() => setLoginModalOpened(false)}
      />
    </>
  )
}

export default forwardRef(NewsItemCommentForm)

const StyledWrapper = styled.div`
  width: 100%;
  form {
    width: 100%;
    display: flex;
    align-items: flex-start;
    &[data-error='true'] {
      padding-bottom: 18px;
    }
    .input-wrapper {
      align-self: stretch;
      position: relative;
      width: 100%;
      margin-left: 12px;
      max-height: 84px;
      .height {
        padding: 9px;
        font-size: 14px;
        line-height: 22px;
        word-break: break-word;
        /* overflow 방지 */
        max-height: 84px;
        overflow: scroll;
      }
      .comment-input {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        textarea {
          height: 100%;
          font-size: 14px;
          line-height: 22px;
        }
        .error {
          bottom: 0;
          transform: translateY(100%);
          position: absolute;
          padding-top: 8px;
          color: #ef2929;
        }
      }
    }
    .btn-wrapper {
      align-self: stretch;
      flex-shrink: 0;
      .btn {
        min-height: 40px;
        height: 100%;
        margin-left: 4px;
      }
    }
  }
`
